export const PHASES = {
  LOBBY: 'lobby',
  DEALING: 'dealing',
  ARRANGING: 'arranging',
  SHOWDOWN: 'showdown',
  RESULTS: 'results',
};

export const initialState = {
  // Room
  gameId: null,
  uid: null,
  phase: null,

  // Players
  players: {},
  dealerUid: null,

  // Game config
  gameMode: null,
  activeRules: {},
  deckCount: 1,
  roundNumber: 0,

  // Hand (current player only)
  myHand: [],
  originalHand: [],
  arrangement: { bottom: [], middle: [], top: [] },

  // Multiplayer state
  readyStatus: {},
  nemCompleted: {},

  // Showdown
  revealedArrangements: {},
  roundResults: {},
  showdownStep: 0,
  sessionTotals: {},  // { [uid]: number } — accumulated across rounds
  roundHistory: {},   // { [roundNumber]: { totals, sessionTotals, arrangements, names, timestamp } }

  // Timer
  timerStartTime: null,
  timerDuration: 90,

  // Between rounds
  betweenRoundsReady: {},
  betweenRoundsTimerStart: null,

  // Dealer succession
  dealerSuccession: null,

  // UI-only
  selectedCard: null,
  dragState: null,
  violationRows: [],
  autoWinHand: null,
  nemPendingDiscardIds: new Set(),
  nemDiscardedCards: [],
  nemNewCardIds: new Set(),

  // Connection
  connected: true,
  dealerConnected: true,
  error: null,
};

export function gameReducer(state, action) {
  switch (action.type) {
    // === Room ===
    case 'SET_ROOM':
      return { ...state, gameId: action.gameId, uid: action.uid };

    case 'CLEAR_ROOM':
      return { ...initialState };

    // === Firebase sync (incoming) ===
    case 'SYNC_PHASE': {
      const newPhase = action.phase;
      // Reset UI state when entering dealing phase (new hand starting).
      // Do NOT clear myHand/originalHand here — SYNC_HAND may have already
      // set the new hand (Firebase listener callback order is not guaranteed).
      // SYNC_HAND handles replacing hand data when it detects a new deal.
      if (newPhase === 'dealing' && state.phase !== 'dealing') {
        return {
          ...state,
          phase: newPhase,
          arrangement: { bottom: [], middle: [], top: [] },
          timerStartTime: null,
          timerDuration: 90,
          nemPendingDiscardIds: new Set(),
          nemDiscardedCards: [],
          nemNewCardIds: new Set(),
          showdownStep: 0,
        };
      }
      return { ...state, phase: newPhase };
    }

    case 'SYNC_META':
      return {
        ...state,
        dealerUid: action.meta.dealerUid ?? state.dealerUid,
        gameMode: action.meta.gameMode ?? state.gameMode,
        activeRules: action.meta.activeRules ?? state.activeRules,
        deckCount: action.meta.deckCount ?? state.deckCount,
        roundNumber: action.meta.roundNumber ?? state.roundNumber,
        // timerStartTime: use value from Firebase directly (null means no active timer)
        timerStartTime: action.meta.timerStartTime !== undefined ? action.meta.timerStartTime : state.timerStartTime,
        timerDuration: action.meta.timerDuration ?? state.timerDuration,
        dealerConnected: action.meta.dealerConnected,
      };

    case 'SYNC_PLAYERS':
      return { ...state, players: action.players };

    case 'SYNC_HAND': {
      // Normalize hand — Firebase may return object with numeric keys instead of array
      const rawHand = action.hand;
      const normalizedHand = Array.isArray(rawHand) ? rawHand : Object.values(rawHand || {});

      // During arranging phase with an existing hand: this is a Nem update.
      // Only add genuinely new cards (ones not already in local hand or arrangement).
      // This check comes BEFORE new-deal detection so that Nem updates during
      // arranging never reset the player's arrangement.
      if (state.phase === 'arranging' && state.originalHand.length > 0) {
        const localCardIds = new Set([
          ...state.myHand.map(c => c.id),
          ...state.arrangement.bottom.filter(Boolean).map(c => c.id),
          ...state.arrangement.middle.filter(Boolean).map(c => c.id),
          ...state.arrangement.top.filter(Boolean).map(c => c.id),
        ]);
        const newCards = normalizedHand.filter(c => !localCardIds.has(c.id));
        if (newCards.length === 0) {
          return state; // No new cards to add
        }
        // New cards from Nem — add to staging only
        return {
          ...state,
          myHand: [...state.myHand, ...newCards],
        };
      }

      // Any other phase (dealing, lobby, results, showdown, or first load):
      // treat as a new deal — replace hand entirely.
      // This is order-independent: works whether SYNC_PHASE("dealing") has
      // fired before or after this SYNC_HAND action.
      return {
        ...state,
        myHand: normalizedHand,
        originalHand: normalizedHand,
        arrangement: { bottom: [], middle: [], top: [] },
        violationRows: [],
        autoWinHand: null,
        nemPendingDiscardIds: new Set(),
        nemDiscardedCards: [],
        nemNewCardIds: new Set(),
        selectedCard: null,
        showdownStep: 0,
      };
    }

    case 'SYNC_READY':
      return { ...state, readyStatus: action.readyStatus };

    case 'SYNC_NEM_COMPLETED': {
      const nemData = action.nemCompleted;
      // If nem completed is empty (cleared for new round), also clear local Nem visual state
      const nemIsCleared = !nemData || Object.keys(nemData).length === 0;
      return {
        ...state,
        nemCompleted: nemData,
        ...(nemIsCleared ? {
          nemDiscardedCards: [],
          nemNewCardIds: new Set(),
          nemPendingDiscardIds: new Set(),
        } : {}),
      };
    }

    case 'SYNC_REVEALED_ARRANGEMENTS':
      return { ...state, revealedArrangements: action.arrangements };

    case 'SYNC_ROUND_RESULTS':
      return { ...state, roundResults: action.results };

    case 'SYNC_ROUND_HISTORY':
      return { ...state, roundHistory: action.roundHistory || {} };

    case 'SYNC_SESSION_TOTALS':
      return { ...state, sessionTotals: action.totals || {} };

    case 'SYNC_BETWEEN_ROUNDS':
      return {
        ...state,
        betweenRoundsReady: action.ready ?? state.betweenRoundsReady,
        betweenRoundsTimerStart: action.timerStartTime ?? state.betweenRoundsTimerStart,
      };

    case 'SYNC_DEALER_SUCCESSION':
      return { ...state, dealerSuccession: action.data };

    case 'SYNC_CONNECTION':
      return { ...state, connected: action.connected };

    // === Local UI state ===
    case 'SET_ARRANGEMENT':
      return { ...state, arrangement: action.arrangement };

    case 'SET_HAND':
      return { ...state, myHand: action.hand };

    case 'MOVE_CARD_TO_ROW': {
      const { card, row } = action;
      // Remove card from hand
      const newHand = state.myHand.filter(c => c.id !== card.id);
      // Remove card from ALL rows first
      const newArrangement = {};
      for (const r of ['bottom', 'middle', 'top']) {
        newArrangement[r] = state.arrangement[r].filter(c => c.id !== card.id);
      }
      // Append card to target row (no sparse arrays)
      newArrangement[row] = [...newArrangement[row], card];
      return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
    }

    case 'MOVE_CARD_TO_STAGING': {
      const { card } = action;
      const newArrangement = { ...state.arrangement };
      for (const r of ['bottom', 'middle', 'top']) {
        newArrangement[r] = state.arrangement[r].filter(c => c.id !== card.id);
      }
      const alreadyInHand = state.myHand.some(c => c.id === card.id);
      const newHand = alreadyInHand ? state.myHand : [...state.myHand, card];
      return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
    }

    case 'AUTO_PLACE_CARD': {
      // Tap-to-place: drop the card into the first non-full row, bottom → middle → top.
      const { card } = action;
      const sizes = { bottom: 5, middle: 5, top: 3 };
      let target = null;
      for (const r of ['bottom', 'middle', 'top']) {
        if (state.arrangement[r].length < sizes[r]) { target = r; break; }
      }
      if (!target) return state; // all rows full — nothing to do
      const newHand = state.myHand.filter(c => c.id !== card.id);
      const newArrangement = {};
      for (const r of ['bottom', 'middle', 'top']) {
        newArrangement[r] = state.arrangement[r].filter(c => c.id !== card.id);
      }
      newArrangement[target] = [...newArrangement[target], card];
      return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
    }

    case 'TAP_STAGING_CARD': {
      const { card } = action;
      // If a row card is already selected → swap: row card goes to staging, tapped staging card takes its slot
      if (state.selectedCard && state.selectedCard.source === 'row') {
        const { row: selRow, index: selIndex } = state.selectedCard;
        const newArrangement = { ...state.arrangement };
        newArrangement[selRow] = state.arrangement[selRow].map((c, i) =>
          i === selIndex ? card : c
        );
        const newHand = state.myHand.filter(c => c.id !== card.id).concat(state.arrangement[selRow][selIndex]);
        return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
      }
      // If same staging card already selected → deselect
      if (state.selectedCard && state.selectedCard.source === 'staging' && state.selectedCard.card.id === card.id) {
        return { ...state, selectedCard: null };
      }
      // Otherwise → select this staging card (highlight it, wait for slot or row tap)
      return { ...state, selectedCard: { source: 'staging', card } };
    }

    case 'TAP_SLOT': {
      // Tap empty slot in a row — append card (avoids sparse arrays from indexed assignment)
      const { row } = action;
      if (!state.selectedCard) return state;
      if (state.selectedCard.source === 'staging') {
        // Place selected staging card into this row
        const card = state.selectedCard.card;
        const newHand = state.myHand.filter(c => c.id !== card.id);
        const newArrangement = { ...state.arrangement };
        newArrangement[row] = [...state.arrangement[row], card];
        return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
      }
      if (state.selectedCard.source === 'row') {
        // Move selected row card to this row
        const { row: selRow, index: selIndex, card } = state.selectedCard;
        const newArrangement = { ...state.arrangement };
        const fromRow = [...state.arrangement[selRow]];
        fromRow.splice(selIndex, 1);
        newArrangement[selRow] = fromRow;
        newArrangement[row] = [...newArrangement[row], card];
        return { ...state, arrangement: newArrangement, selectedCard: null };
      }
      return state;
    }

    case 'TAP_ROW_CARD': {
      const { card, row, index } = action;
      // If a staging card is selected → place it here, bump this row card back to staging
      if (state.selectedCard && state.selectedCard.source === 'staging') {
        const stagingCard = state.selectedCard.card;
        const newArrangement = { ...state.arrangement };
        newArrangement[row] = state.arrangement[row].map((c, i) => i === index ? stagingCard : c);
        const newHand = state.myHand.filter(c => c.id !== stagingCard.id).concat(card);
        return { ...state, myHand: newHand, arrangement: newArrangement, selectedCard: null };
      }
      // If same row card already selected → deselect
      if (state.selectedCard && state.selectedCard.source === 'row' &&
          state.selectedCard.row === row && state.selectedCard.index === index) {
        return { ...state, selectedCard: null };
      }
      // If a different row card is selected → swap them
      if (state.selectedCard && state.selectedCard.source === 'row') {
        const { row: selRow, index: selIndex, card: selCard } = state.selectedCard;
        const newArrangement = { ...state.arrangement };
        newArrangement[selRow] = state.arrangement[selRow].map((c, i) => i === selIndex ? card : c);
        newArrangement[row] = state.arrangement[row].map((c, i) => i === index ? selCard : c);
        return { ...state, arrangement: newArrangement, selectedCard: null };
      }
      // Nothing selected → select this row card
      return { ...state, selectedCard: { source: 'row', row, index, card } };
    }

    case 'RETURN_ROW_CARD_TO_STAGING': {
      const { row, index } = action;
      const card = state.arrangement[row]?.[index];
      if (!card) return state;
      const newArrangement = { ...state.arrangement };
      const newRow = [...state.arrangement[row]];
      newRow.splice(index, 1);
      newArrangement[row] = newRow;
      return { ...state, myHand: [...state.myHand, card], arrangement: newArrangement, selectedCard: null };
    }

    case 'SWAP_CARDS': {
      const { card1, card2, row1, row2 } = action;
      const newArrangement = { ...state.arrangement };
      newArrangement[row1] = state.arrangement[row1].map(c => c.id === card1.id ? card2 : c);
      newArrangement[row2] = state.arrangement[row2].map(c => c.id === card2.id ? card1 : c);
      return { ...state, arrangement: newArrangement };
    }

    case 'RESET_ARRANGEMENT':
      return {
        ...state,
        myHand: [...state.originalHand],
        arrangement: { bottom: [], middle: [], top: [] },
        selectedCard: null,
      };

    case 'SET_SELECTED_CARD':
      return { ...state, selectedCard: action.card };

    case 'SET_DRAG_STATE':
      return { ...state, dragState: action.dragState };

    case 'SET_VIOLATION_ROWS':
      return { ...state, violationRows: action.rows };

    case 'SET_AUTO_WIN_HAND':
      return { ...state, autoWinHand: action.autoWinHand };

    case 'SET_SHOWDOWN_STEP':
      return { ...state, showdownStep: action.step };

    // === Nem ===
    case 'NEM_PENDING_ADD': {
      const newSet = new Set(state.nemPendingDiscardIds);
      newSet.add(action.cardId);
      return { ...state, nemPendingDiscardIds: newSet };
    }

    case 'NEM_PENDING_REMOVE': {
      const newSet = new Set(state.nemPendingDiscardIds);
      newSet.delete(action.cardId);
      return { ...state, nemPendingDiscardIds: newSet };
    }

    case 'NEM_DISCARD': {
      const { discardedIds, discardedCards = [], newCards } = action;
      const discardSet = new Set(discardedIds);
      // Remove discarded from staging and add new cards directly.
      // Deduplicate: SYNC_HAND may have already added the drawn cards if it fired first.
      const handAfterDiscard = state.myHand.filter(c => !discardSet.has(c.id));
      const existingIds = new Set(handAfterDiscard.map(c => c.id));
      const newHand = [
        ...handAfterDiscard,
        ...newCards.filter(c => !existingIds.has(c.id)),
      ];
      // Remove discarded from rows
      const newArrangement = {
        bottom: state.arrangement.bottom.filter(c => !discardSet.has(c.id)),
        middle: state.arrangement.middle.filter(c => !discardSet.has(c.id)),
        top: state.arrangement.top.filter(c => !discardSet.has(c.id)),
      };
      // BUG-9 fix: update originalHand so RESET_ARRANGEMENT uses post-Nem hand,
      // not the pre-Nem hand (which would bring back discarded cards).
      const newOriginalHand = [
        ...state.originalHand.filter(c => !discardSet.has(c.id)),
        ...newCards,
      ];
      return {
        ...state,
        myHand: newHand,
        originalHand: newOriginalHand,
        arrangement: newArrangement,
        nemDiscardedCards: discardedCards,
        nemNewCardIds: new Set(newCards.map(c => c.id)),
        nemPendingDiscardIds: new Set(),
      };
    }

    case 'CLEAR_NEM_STATE':
      return { ...state, nemDiscardedCards: [], nemNewCardIds: new Set() };

    // === Error ===
    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      console.warn('Unknown action:', action.type);
      return state;
  }
}
